import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Box,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import PageContainer from "../components/common/PageContainer";
import TemplateForm from "../components/templates/TemplateForm";
import TemplateDetail from "../components/templates/TemplateDetail";
import {
  createTemplate,
  validateTemplate,
  TemplateFactory,
  getTemplateStats,
} from "../models/Template";
import { templateService } from "../services/templateService";

/**
 * Enhanced page component for creating new templates with manager features
 */
const TemplateCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saveStatus, setSaveStatus] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    if (location.state) {
      console.log("New Template Location: ", location.state);
      setTemplate(location.state);
    }
  }, [location]);

  // Check if we're duplicating from an existing template
  const duplicateFromId = searchParams.get("duplicate");
  const templateType = searchParams.get("type"); // 'basic', 'inspection', 'maintenance'

  // Initialize with template factory if type is specified
  const getInitialTemplate = () => {
    if (templateType === "inspection") {
      return TemplateFactory.createInspection("New Inspection Template");
    } else if (templateType === "maintenance") {
      return TemplateFactory.createMaintenance("New Maintenance Template");
    } else if (templateType === "basic") {
      return TemplateFactory.createBasic("New Template");
    }
    return null;
  };

  const initialTemplate = getInitialTemplate();

  const handleSave = async (templateData) => {
    if (isSaving) return; // Prevent double submission

    try {
      setIsSaving(true);
      setValidationErrors([]);

      // Validate the template data
      const validation = validateTemplate(templateData);

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setSaveStatus({
          open: true,
          message: "Please fix the validation errors before saving.",
          severity: "error",
        });
        return;
      }

      // Prepare template data with required fields
      // console.log(templateData);
      const templateToSave = {
        ...templateData,
        createdBy: "current_user_id", // This would come from auth context
        // Ensure we have a country field for Lambda
        country: templateData.metadata?.countries?.[0] || "IT",
      };

      // Call the actual API service
      const savedTemplate = await templateService.createTemplate(
        templateToSave
      );

      // Show success message
      setSaveStatus({
        open: true,
        message: `Template "${savedTemplate.name}" created successfully!`,
        severity: "success",
      });

      // Navigate to templates list after a short delay
      setTimeout(() => {
        navigate("/templates");
      }, 1500);
    } catch (error) {
      console.error("Error saving template:", error);

      // Handle specific API errors
      let errorMessage = "Failed to save template. Please try again.";

      if (error.message && error.message.includes("Required field")) {
        errorMessage = error.message;
      } else if (error.getUserMessage) {
        errorMessage = error.getUserMessage();
      }

      setSaveStatus({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/templates");
  };

  const handlePreview = (templateData) => {
    // Create a temporary template for preview
    const tempTemplate = createTemplate({
      ...templateData,
      createdBy: "current_user_id",
    });

    setPreviewTemplate(tempTemplate);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewTemplate(null);
  };

  const handleCloseSnackbar = () => {
    setSaveStatus((prev) => ({ ...prev, open: false }));
  };

  const getPageTitle = () => {
    if (duplicateFromId) return "Duplicate Template";
    if (templateType)
      return `Create ${
        templateType.charAt(0).toUpperCase() + templateType.slice(1)
      } Template`;
    return "Create New Template";
  };

  const getBreadcrumbs = () => {
    const breadcrumbs = [{ text: "Templates", path: "/templates" }];

    if (duplicateFromId) {
      breadcrumbs.push({ text: "Duplicate Template" });
    } else {
      breadcrumbs.push({ text: "Create New Template" });
    }

    return breadcrumbs;
  };

  return (
    <PageContainer
      title={getPageTitle()}
      subtitle={
        templateType
          ? `Using ${templateType} template starter`
          : "Build a custom template from scratch"
      }
      breadcrumbs={getBreadcrumbs()}
      actions={
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleCancel}
        >
          Back to Templates
        </Button>
      }
    >
      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Please fix the following errors:
          </Typography>
          <List dense>
            {validationErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemText primary={`• ${error}`} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* Template Type Info */}
      {templateType && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              You're creating a template using the{" "}
              <strong>{templateType}</strong> starter.
            </Typography>
            <Chip
              label={`${templateType} template`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Alert>
      )}

      {/* Enhanced Template Form */}
      <Box sx={{ position: "relative" }}>
        <TemplateForm
          template={template}
          onSave={handleSave}
          onCancel={handleCancel}
          onPreview={handlePreview}
          isLoading={isSaving}
          disabled={isSaving}
        />

        {/* Loading overlay */}
        {isSaving && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Saving template...
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: "80vh" },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PreviewIcon />
            <Typography variant="h6">Template Preview</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {previewTemplate && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This is how your template will appear to users. You can still
                make changes after closing this preview.
              </Alert>

              <TemplateDetail
                template={previewTemplate}
                onEdit={() => {}}
                onDelete={() => {}}
                onDuplicate={() => {}}
                onUse={() => {}}
                onToggleFavorite={() => {}}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePreview} disabled={isSaving}>
            Close Preview
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleClosePreview();
              handleSave(previewTemplate);
            }}
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Snackbar */}
      <Snackbar
        open={saveStatus.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={saveStatus.severity}
          variant="filled"
        >
          {saveStatus.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default TemplateCreate;
